
import React from 'react';
import { VendorProfile } from '../types';

interface QuickMessageModalProps {
  vendor: VendorProfile;
  onClose: () => void;
}

const MESSAGE_TEMPLATES = [
  {
    id: 'pro_expiring',
    title: 'Plano PRO Vencendo',
    description: 'Lembrete de renovação de assinatura.',
    icon: '💎',
    color: 'bg-brand-orange/10 text-brand-orange',
    text: (v: VendorProfile) => `Olá *${v.responsible}*! 👋 Notamos que o Plano PRO da sua loja *${v.name}* no Compre em Guarulhos está próximo do vencimento. Vamos renovar para manter seu Selo Verificado e anúncios ilimitados?`
  },
  {
    id: 'no_ads',
    title: 'Sem Anúncios',
    description: 'Incentivo para cadastrar produtos.',
    icon: '📦',
    color: 'bg-brand-blue/10 text-brand-blue',
    text: (v: VendorProfile) => `Oi *${v.responsible}*! Notamos que a *${v.name}* ainda não tem anúncios ativos na nossa vitrine. Que tal subir suas primeiras ofertas hoje para começar a receber clientes? 🚀`
  },
  {
    id: 'incomplete_profile',
    title: 'Perfil Incompleto',
    description: 'Dica para melhorar a conversão.',
    icon: '👤',
    color: 'bg-purple-100 text-purple-600',
    text: (v: VendorProfile) => `Olá! Para passar mais confiança aos clientes em Guarulhos, recomendamos completar o perfil da *${v.name}* com uma logo oficial e uma bio detalhada. Isso ajuda muito nas vendas!`
  },
  {
    id: 'welcome',
    title: 'Boas-vindas',
    description: 'Saudação para novos parceiros.',
    icon: '🎉',
    color: 'bg-green-100 text-green-600',
    text: (v: VendorProfile) => `Seja muito bem-vindo(a) ao Compre em Guarulhos! Ficamos felizes em ter a *${v.name}* conosco. Qualquer dúvida na configuração da sua vitrine, pode contar comigo aqui no suporte.`
  },
  {
    id: 'inactivity',
    title: 'Inatividade',
    description: 'Recuperar lojista sumido.',
    icon: '⏰',
    color: 'bg-red-100 text-red-500',
    text: (v: VendorProfile) => `Sentimos sua falta, *${v.responsible}*! Faz tempo que não vemos novas ofertas da *${v.name}*. Que tal atualizar sua vitrine com as novidades da semana?`
  }
];

const QuickMessageModal: React.FC<QuickMessageModalProps> = ({ vendor, onClose }) => {
  const handleSend = (templateText: string) => {
    window.open(`https://wa.me/${vendor.whatsapp}?text=${encodeURIComponent(templateText)}`, '_blank');
    onClose();
  };

  return (
    <div className="fixed inset-0 z-[400] flex items-center justify-center p-4 sm:p-6 animate-in fade-in duration-300">
      <div className="absolute inset-0 bg-brand-navy/95 backdrop-blur-xl" onClick={onClose} />
      
      <div className="relative bg-white w-full max-w-2xl rounded-[3.5rem] shadow-2xl overflow-hidden animate-in zoom-in duration-300 flex flex-col max-h-[90vh]">
        <div className="p-10 border-b border-gray-50 flex items-center justify-between bg-gray-50/50">
          <div>
            <span className="text-brand-orange text-[10px] font-black uppercase tracking-[0.3em] mb-1 block">Atendimento ao Parceiro</span>
            <h3 className="text-2xl font-title font-black text-brand-navy uppercase italic tracking-tighter leading-none italic">Mensagens Rápidas</h3>
          </div>
          <button onClick={onClose} className="p-4 text-gray-300 hover:text-brand-navy transition-colors">
            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M6 18L18 6M6 6l12 12"/></svg>
          </button>
        </div>

        <div className="p-10 overflow-y-auto no-scrollbar flex-1 space-y-6">
          <div className="flex items-center gap-4 p-6 bg-brand-navy/5 rounded-3xl border border-brand-navy/5 mb-4">
             <img src={vendor.avatar_url} className="w-12 h-12 rounded-xl object-cover" />
             <div>
               <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Enviando para:</p>
               <p className="text-sm font-black text-brand-navy uppercase italic">{vendor.name} ({vendor.responsible})</p>
             </div>
          </div>

          <div className="grid grid-cols-1 gap-4">
            {MESSAGE_TEMPLATES.map(item => (
              <button 
                key={item.id}
                onClick={() => handleSend(item.text(vendor))}
                className="group flex items-center gap-6 p-6 rounded-[2.5rem] border border-gray-100 bg-white hover:border-brand-orange hover:shadow-xl hover:shadow-brand-orange/5 transition-all text-left"
              >
                <div className={`w-14 h-14 rounded-2xl flex items-center justify-center text-2xl shrink-0 group-hover:scale-110 transition-transform ${item.color}`}>
                  {item.icon}
                </div>
                <div className="flex-1">
                  <h4 className="text-xs font-black text-brand-navy uppercase tracking-widest mb-1">{item.title}</h4>
                  <p className="text-[10px] text-gray-400 font-bold uppercase tracking-tight">{item.description}</p>
                </div>
                <div className="bg-gray-50 p-3 rounded-full opacity-0 group-hover:opacity-100 transition-opacity">
                   <svg className="w-5 h-5 text-brand-orange" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L0 24l6.335-1.662c1.72.937 3.659 1.432 5.632 1.433h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                </div>
              </button>
            ))}
          </div>
        </div>

        <div className="p-8 bg-gray-50/50 flex justify-center border-t border-gray-100">
           <button onClick={onClose} className="text-gray-300 font-black text-[10px] uppercase tracking-widest hover:text-brand-navy transition-colors">Fechar</button>
        </div>
      </div>
    </div>
  );
};

export default QuickMessageModal;
